/**
 * Pure helpers for the charts and indicator figures (components/ui.tsx: Bars, Kpi). No DOM here, so the geometry is
 * unit-tested: bars never leave the plot area, the scale always leaves headroom, labels never overlap.
 */

export interface Scale {
  min: number;
  max: number;
  step: number;
  ticks: number[];
}

const NICE_STEPS = [1, 2, 2.5, 5];

/**
 * "Nice" value axis that always contains zero. The extreme ticks sit at least 5 % beyond the data, so the tallest
 * (or deepest) bar never touches the edge of the plot. Steps are 1, 2, 2.5 or 5 × 10^k; `integer` keeps them whole
 * numbers (vehicles and centavos have no fractions).
 */
export function niceScale(
  dataMin: number,
  dataMax: number,
  opts: { maxIntervals?: number; integer?: boolean } = {},
): Scale {
  // Two intervals at least: with data on both sides of zero, one interval could never hold both extremes.
  const maxIntervals = Math.max(2, Math.floor(opts.maxIntervals ?? 4));
  const lo = Math.min(0, Number.isFinite(dataMin) ? dataMin : 0);
  const hi = Math.max(0, Number.isFinite(dataMax) ? dataMax : 0);
  if (lo === 0 && hi === 0) return { min: 0, max: 1, step: 1, ticks: [0] };
  const top = hi * 1.05;
  const bottom = lo * 1.05;
  let magnitude = 10 ** Math.floor(Math.log10((top - bottom) / maxIntervals));
  for (;;) {
    for (const m of NICE_STEPS) {
      const step = m * magnitude;
      // 2.5 × 10^0 is ≥ 1 but still fractional: "integer" needs a whole step, not only a step of at least one.
      if (opts.integer && !Number.isInteger(clean(step))) continue;
      // Ticks are whole multiples of the step, so zero is always exactly one of them (the baseline).
      const kMax = Math.ceil(top / step);
      const kMin = Math.floor(bottom / step);
      if (kMax - kMin <= maxIntervals) {
        const ticks = Array.from({ length: kMax - kMin + 1 }, (_, i) => clean((kMin + i) * step));
        return { min: ticks[0]!, max: ticks.at(-1)!, step: clean(step), ticks };
      }
    }
    magnitude *= 10;
  }
}

/** Drops floating-point noise (0.30000000000000004) and negative zero from computed tick values. */
function clean(n: number): number {
  const v = Number(n.toPrecision(12));
  return v === 0 ? 0 : v;
}

// Intl separates "R$" and "mil" with a no-break space (U+00A0, U+202F in some locales); the rest of the interface
// uses a plain one (formatBRL). Built from char codes so the source holds no invisible characters.
const NO_BREAK_SPACES = new RegExp(`[${String.fromCharCode(0xa0, 0x202f)}]`, 'g');
const plainSpaces = (s: string) => s.replace(NO_BREAK_SPACES, ' ');
const COMPACT = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });
const COMPACT_BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const BRL_WHOLE = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
});
const BRL_CENTS = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Axis ticks are k × (1, 2, 2,5 or 5) × 10^n with |k| ≤ 5, so at most three significant digits: two decimals in
// compact units are always exact. With one decimal the 1 250 tick of a 250-step axis would read "1,3 mil".
const AXIS = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 2 });
const AXIS_BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 2,
});

/** "1,2 mil", "3,4 mi", "12,3 bi" — figures too long for their box. */
export const compactNumber = (n: number): string => plainSpaces(COMPACT.format(n));

function brlFrom(cents: number, compact: Intl.NumberFormat): string {
  const reais = cents / 100;
  if (Math.abs(reais) >= 1000) return plainSpaces(compact.format(reais));
  return plainSpaces((Number.isInteger(reais) ? BRL_WHOLE : BRL_CENTS).format(reais));
}

/**
 * Compact currency from centavos: "R$ 250", "R$ 7,50", "R$ 1,2 mil", "R$ 123,5 bi". Display only — the division by
 * 100 never feeds a calculation; exact amounts stay with formatBRL.
 */
export const compactBrl = (cents: number): string => brlFrom(cents, COMPACT_BRL);

/** Value-axis labels for counts: compact and exact for every tick of niceScale ("1,25 mil"). */
export const axisNumber = (n: number): string => plainSpaces(AXIS.format(n));

/** Value-axis labels for money charts, from centavos: compact and exact for every tick ("R$ 1,25 mi"). */
export const axisBrl = (cents: number): string => brlFrom(cents, AXIS_BRL);

const MONEY_TEXT = /^(-)?R\$\s?(\d{1,3}(?:\.\d{3})*),(\d{2})$/;
const COUNT_TEXT = /^(-)?(\d{1,3}(?:\.\d{3})+|\d+)$/;

/**
 * Shorter form of a figure already formatted in pt-BR ("R$ 123.456.789.012,34" → "R$ 123,5 bi",
 * "12.345.678" → "12,3 mi"), or null when the text is not such a figure or is below a thousand (nothing to gain).
 */
export function compactFigure(text: string): string | null {
  const t = text.trim();
  let compact: string | null = null;
  const money = MONEY_TEXT.exec(t);
  if (money) {
    const cents = Number(money[2]!.replace(/\./g, '')) * 100 + Number(money[3]);
    if (Number.isSafeInteger(cents) && cents >= 100_000) compact = compactBrl(money[1] ? -cents : cents);
  } else {
    const count = COUNT_TEXT.exec(t);
    if (count) {
      const n = Number(count[2]!.replace(/\./g, ''));
      if (Number.isSafeInteger(n) && n >= 1000) compact = compactNumber(count[1] ? -n : n);
    }
  }
  return compact !== null && compact.length < t.length ? compact : null;
}

/**
 * Conservative width of a label in Montserrat at weight 400 with tabular figures (.chart text). Measured in Chrome at
 * 11 px: a digit is 7.70 px (0.700 em), "DOM" 28.83 px (0.874 em per capital). Used to size the y-axis gutter and to thin
 * x labels without measuring the DOM; an estimate below the real width cuts the label at the edge of the drawing.
 */
export function textWidth(text: string, fontPx: number): number {
  let em = 0;
  for (const ch of text) {
    if (/[A-ZÀ-Ý]/.test(ch)) em += 0.88;
    else if (/\d/.test(ch)) em += 0.71;
    else if (/[mw]/.test(ch)) em += 0.92;
    else if (/[\s.,:;'’]/.test(ch)) em += 0.32;
    else em += 0.62;
  }
  return em * fontPx;
}

export interface BarInput {
  label: string;
  value: number;
  previous?: number | null;
}
export interface BarRect {
  index: number;
  kind: 'current' | 'previous';
  x: number;
  y: number;
  width: number;
  height: number;
}
export interface BarLayout {
  plot: { left: number; top: number; right: number; bottom: number };
  scale: Scale;
  zeroY: number;
  ticks: { value: number; y: number; label: string }[];
  bars: BarRect[];
  xLabels: { index: number; x: number; y: number; text: string }[];
}

export const CHART_FONT_PX = 11;
const PAD_TOP = 10;
const PAD_RIGHT = 4;
const X_AXIS = CHART_FONT_PX + 12;

/**
 * Bar chart geometry in real pixels for the measured width: the text keeps its size on a phone instead of being
 * scaled down with the whole drawing. Values may be negative (the zero line then sits inside the plot).
 */
export function layoutBars(
  data: BarInput[],
  opts: { width: number; height: number; maxBarWidth: number; axisFormat: (n: number) => string },
): BarLayout {
  const { width, height } = opts;
  const finite = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isFinite(n);
  const values = data.flatMap((d) => [d.value, d.previous]).filter(finite);
  const hasPrev = data.some((d) => finite(d.previous));
  const scale = niceScale(Math.min(0, ...values), Math.max(0, ...values), {
    integer: values.every((v) => Number.isInteger(v)),
    maxIntervals: height < 150 ? 3 : 5,
  });
  const tickLabels = scale.ticks.map(opts.axisFormat);
  const gutter = Math.min(
    width * 0.45,
    Math.max(24, ...tickLabels.map((l) => textWidth(l, CHART_FONT_PX))) + 10,
  );
  const plot = {
    left: gutter,
    top: PAD_TOP,
    right: Math.max(gutter + 1, width - PAD_RIGHT),
    bottom: Math.max(PAD_TOP + 1, height - X_AXIS),
  };
  const plotH = plot.bottom - plot.top;
  const span = scale.max - scale.min;
  const clampY = (y: number) => Math.min(plot.bottom, Math.max(plot.top, y));
  const yOf = (v: number) => clampY(plot.top + ((scale.max - v) / span) * plotH);
  const zeroY = yOf(0);

  const n = Math.max(1, data.length);
  const slot = (plot.right - plot.left) / n;
  const group = Math.min(slot * 0.72, hasPrev ? opts.maxBarWidth * 2 + 2 : opts.maxBarWidth);
  const gap = hasPrev && group > 6 ? 2 : 0;
  const barW = hasPrev ? (group - gap) / 2 : group;
  const rect = (index: number, kind: BarRect['kind'], x: number, v: number): BarRect => {
    let top = Math.min(yOf(v), zeroY);
    let h = Math.abs(yOf(v) - zeroY);
    // A non-zero value stays visible as a 1 px sliver, still inside the plot.
    if (v !== 0 && h < 1) {
      h = Math.min(1, plotH);
      top = v > 0 ? Math.max(plot.top, zeroY - h) : Math.min(zeroY, plot.bottom - h);
    }
    return { index, kind, x, y: top, width: barW, height: h };
  };
  const bars: BarRect[] = [];
  data.forEach((d, i) => {
    const gx = plot.left + slot * (i + 0.5) - group / 2;
    if (hasPrev && finite(d.previous)) bars.push(rect(i, 'previous', gx, d.previous));
    bars.push(rect(i, 'current', hasPrev ? gx + barW + gap : gx, finite(d.value) ? d.value : 0));
  });

  // One label every `every` slots, so neighbours never overlap whatever the width and the number of points; a label
  // that would cross the right edge is left out rather than cut.
  const labelW = Math.max(0, ...data.map((d) => textWidth(d.label, CHART_FONT_PX))) + 6;
  const every = Math.max(1, Math.ceil(labelW / slot));
  const xLabels = data
    .map((d, i) => ({ index: i, x: plot.left + slot * (i + 0.5), y: plot.bottom + 15, text: d.label }))
    .filter((l) => l.index % every === 0 && l.x + textWidth(l.text, CHART_FONT_PX) / 2 <= width);

  return {
    plot,
    scale,
    zeroY,
    ticks: scale.ticks.map((value, i) => ({ value, y: yOf(value), label: tickLabels[i]! })),
    bars,
    xLabels,
  };
}
